/**
 * useUserTypes — ViewModel for the /iam/user-types screen.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination" + .claude/ui/
 * 07-per-screen-flows-iam.md §3/§5/§7.
 *
 * Composes the shared `usePaginatedList<T>` hook so paging state +
 * filter-reset semantics are uniform across every list screen.
 * Owns the create/update/delete mutations on top of that read path.
 *
 * 2026-05-14: direct `screen_ids` removed (migration 0028). User_types
 * now link to `group_ids` (Layer 2c) — groups own the screen_action
 * bundles. The list payload's `extras` is the slim active-groups
 * catalogue, not the screens catalogue.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  CreateUserTypeInput,
  GroupCatalogueRow,
  UpdateUserTypeInput,
  UserTypeDto
} from '../models/user-type.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/user-type.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type {
  CreateUserTypeInput,
  GroupCatalogueRow,
  UpdateUserTypeInput,
  UserTypeDto
} from '../models/user-type.model'

export interface UseUserTypesReturn {
  readonly items: UserTypeDto[]
  readonly groups: GroupCatalogueRow[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateUserTypeInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    code: string,
    patch: UpdateUserTypeInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (code: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: UserTypeDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly groups: GroupCatalogueRow[]
  }
}

export function useUserTypes(): UseUserTypesReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<UserTypeDto, GroupCatalogueRow[]>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.IAMUSERTYPES}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: body.data.groups
        }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateUserTypeInput) => {
      const r = await mutateJson<UserTypeDto>(
        authedFetch,
        APIROUTES.IAMUSERTYPES,
        'POST',
        {
          code: input.code,
          name: input.name,
          description: input.description,
          landing_path: input.landing_path,
          is_active: input.is_active,
          group_ids: input.group_ids
        }
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (code: string, patch: UpdateUserTypeInput) => {
      const r = await mutateJson<UserTypeDto>(
        authedFetch,
        fillPath(APIROUTES.IAMUSERTYPEITEM, { code }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.code === code, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (code: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.IAMUSERTYPEITEM, { code }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.code === code)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    groups: paged.extras ?? [],
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
